import { Treinnummer, TreinserieFormData, Treinserie as TreinserieType, useGetDossierOpstartplanById } from "@/entities/opstartplan";
import { useGetStationDisplayName } from "@/entities/station";
import { TreinserieDisplay } from "@/features/infra";
import { ActionsDropdown, Button, DropdownMenuItem, ErrorDisplay, Icon, Skeleton } from "@/shared/components";
import { cn, formatDateTimeRelative, hourFormatInAmsterdam } from "@/shared/utils";
import { useState } from "react";
import { RoleGate, UserRole } from "../auth";
import { NewEditOpstartplanDialog } from "./newedit-opstartplan-dialog";
import { TreinserieForm } from "./treinserie-form";

type OpstartplanDetailsProps = {
    dossierNummer: string;
};

export function OpstartplanDetails({ dossierNummer }: OpstartplanDetailsProps) {
    const [opstartplanDialogOpen, setOpstartplanDialogOpen] = useState(false);
    const { data: opstartplan, isPending, error } = useGetDossierOpstartplanById(dossierNummer);

    if (isPending) {
        return (
            <div>
                <Skeleton className="mb-4 h-7 w-32" />
                <div className="space-y-3 py-4">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                </div>
            </div>
        );
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van Opstartplan" />;
    }

    return (
        <div>
            <div className="flex justify-between">
                <h2 className="text-content-emphasis text-xl font-bold">Opstartplan</h2>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {opstartplan && (
                        <Button variant="secondary" size="s" onClick={() => setOpstartplanDialogOpen(true)}>
                            Opstartplan aanpassen
                        </Button>
                    )}
                </RoleGate>
            </div>
            <div className="pt-4">
                {!opstartplan ? (
                    <div className="grid justify-items-start gap-4">
                        <p className="text-content-subtle">Er is geen opstartplan toegevoegd</p>
                        <RoleGate roles={[UserRole.REGIEVOERDER]}>
                            <Button variant="secondary" size="s" onClick={() => setOpstartplanDialogOpen(true)}>
                                Opstartplan toevoegen
                            </Button>
                        </RoleGate>
                    </div>
                ) : (
                    <div className="grid gap-2">
                        {opstartplan.toelichting && (
                            <div>
                                <h3 className="text-s mb-1 font-bold">Toelichting regievoerder</h3>
                                <p data-testid="opstartplan-toelichting">{opstartplan.toelichting}</p>
                            </div>
                        )}
                        <Treinseries treinseries={opstartplan.treinseries} />
                    </div>
                )}
            </div>
            {opstartplanDialogOpen && (
                <NewEditOpstartplanDialog
                    dossierId={dossierNummer}
                    open={opstartplanDialogOpen}
                    onOpenChange={setOpstartplanDialogOpen}
                    opstartplan={opstartplan}
                />
            )}
        </div>
    );
}

type TreinseriesProps = {
    className?: string;
    treinseries: TreinserieType[];
    actionsEnabled?: boolean;
    onDelete?: (serie: number, nummer: number) => void;
    onEdit?: (data: TreinserieFormData, serie: number) => void;
};

export function Treinseries({ treinseries, className, actionsEnabled = false, onDelete, onEdit }: Readonly<TreinseriesProps>) {
    return (
        <>
            <h3 className="sr-only">Treinseries</h3>
            <div className={cn("flex flex-col gap-2", className)} data-testid="opstartplan-series">
                {treinseries.map((item) => (
                    <Treinserie key={item.serie} treinserie={item} onDelete={onDelete} onEdit={onEdit} actionsEnabled={actionsEnabled} />
                ))}
            </div>
        </>
    );
}

type TreinserieProps = {
    treinserie: TreinserieType;
    actionsEnabled: boolean;
    onEdit?: (data: TreinserieFormData, serie: number) => void;
    onDelete?: (serie: number, number: number) => void;
};

function Treinserie({ treinserie, actionsEnabled = false, onEdit, onDelete }: Readonly<TreinserieProps>) {
    const handleDelete = (nummer: number) => {
        onDelete?.(treinserie.serie, nummer);
    };

    const handleEdit = (data: TreinserieFormData) => {
        onEdit?.(data, treinserie.serie);
    };

    return (
        <div data-testid={`opstartplan-serie-${treinserie.serie}`}>
            <h4 className="text-l text-content-emphasis font-medium">
                <span className="flex shrink flex-row items-center gap-1">
                    Serie <TreinserieDisplay nummer={treinserie.serie} variant="badge" />
                </span>
            </h4>
            <ol data-testid="opstartplan-treinnummers">
                {treinserie.treinnummers.map((trein) => (
                    <Trein key={trein.nummer} trein={trein} onEdit={handleEdit} onDelete={handleDelete} actionsEnabled={actionsEnabled} />
                ))}
            </ol>
        </div>
    );
}

type Treinprops = {
    trein: Treinnummer;
    actionsEnabled: boolean;
    onDelete?: (treinNummer: number) => void;
    onEdit?: (data: TreinserieFormData) => void;
};

function Trein({ trein, onEdit, onDelete, actionsEnabled = false }: Treinprops) {
    const [isEditing, setIsEditing] = useState(false);

    const getStationDisplayName = useGetStationDisplayName("stationCodePrefixed");
    const displayValueVertrektijd = trein.vertrekDatumTijd ? formatDateTimeRelative(trein.vertrekDatumTijd) : undefined;
    const formValueVertrektijd = trein.vertrekDatumTijd ? hourFormatInAmsterdam(trein.vertrekDatumTijd) : undefined;

    if (isEditing) {
        return (
            <TreinserieForm
                defaultValues={{
                    traject: trein.traject,
                    vertrekDatumTijd: formValueVertrektijd,
                    nummer: String(trein.nummer),
                }}
                onCancel={() => setIsEditing(false)}
                onSubmit={(data: TreinserieFormData) => {
                    onEdit?.(data);
                    setIsEditing(false);
                }}
            />
        );
    }

    const isTrajectValid = !!trein.traject.vanStation && !!trein.traject.naarStation;

    return (
        <li key={trein.nummer} className="flex items-center gap-3 py-2" data-testid={`opstartplan-treinnummer-li-${trein.nummer}`}>
            <Icon name="train" className="text-content-emphasis" />
            <div className="flex flex-col">
                <span className="text-content-emphasis" data-testid="opstartplan-treinnummer">
                    {trein.nummer}
                </span>
                <div
                    className="text-content-subtle text-s"
                    data-testid={`opstartplan-route-${trein.traject.vanStation}-${trein.traject.naarStation}`.toLowerCase()}
                >
                    <span>{getStationDisplayName(trein.traject.vanStation)}</span> →{" "}
                    <span>{getStationDisplayName(trein.traject.naarStation)}</span>
                </div>
                {!isTrajectValid && (
                    <span className="text-destructive text-s flex items-center gap-2">
                        {!trein.traject.vanStation && !trein.traject.naarStation && <span>Van en naar station ontbreken</span>}
                        {!trein.traject.vanStation && trein.traject.naarStation && <span>Van station ontbreekt</span>}
                        {trein.traject.vanStation && !trein.traject.naarStation && <span>Naar station ontbreekt</span>}
                    </span>
                )}
            </div>
            <div className="ml-auto flex flex-col text-right">
                <span className="text-content-subtle">vertrek</span>
                <time dateTime={trein.vertrekDatumTijd?.toISOString()}>
                    {displayValueVertrektijd ?? (
                        <span>
                            <Icon name="alert-filled" size="xs" className="text-content-warning mr-1" />
                            ontbreekt
                        </span>
                    )}
                </time>
            </div>
            {actionsEnabled && (
                <ActionsDropdown>
                    {onEdit && <DropdownMenuItem onSelect={() => setIsEditing(true)}>Trein aanpassen</DropdownMenuItem>}
                    {onDelete && (
                        <DropdownMenuItem
                            onSelect={() => {
                                onDelete(trein.nummer);
                            }}
                        >
                            Trein verwijderen
                        </DropdownMenuItem>
                    )}
                </ActionsDropdown>
            )}
        </li>
    );
}
